import { useForm, useStore } from '@tanstack/react-form';
import { confirmPasswordReset } from 'firebase/auth';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate, useRouteError } from 'react-router';
import { z } from 'zod';

import { CardContent, Label } from '@virtual-care/shadcn';
import {
  AuthLayout,
  FieldMessage,
  FormMessage,
  PasswordInput,
  TheButton,
} from '@virtual-care/ui';

import { auth } from '../firebase.tsx';

const schema = z.object({
  password: z.string().min(8, { message: 'shared.validation.passwordMin' }),
  confirmPassword: z
    .string()
    .min(8, { message: 'shared.validation.confirmPasswordRequired' }),
});

interface ResetPasswordLoaderData {
  email: string;
  oobCode: string;
}

export default function B2BResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { email, oobCode } = useLoaderData() as ResetPasswordLoaderData;

  const form = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: schema.superRefine(async (data, ctx) => {
        if (data.password !== data.confirmPassword) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'auth.b2bResetPassword.errors.mismatch',
            path: ['confirmPassword'],
          });
          return false;
        }

        try {
          await confirmPasswordReset(auth, oobCode, data.password);
        } catch {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'auth.b2bResetPassword.errors.genericReset',
            path: ['form'],
          });
        }
        return false;
      }),
    },
  });

  const isSubmitSuccessful = useStore(
    form.store,
    (state) => state.isSubmitSuccessful,
  );

  useEffect(() => {
    if (isSubmitSuccessful) {
      const timer = setTimeout(() => {
        navigate('/b2b-login');
      }, 2000);
      return () => clearTimeout(timer);
    }
    return () => {
      //
    };
  }, [isSubmitSuccessful, navigate]);

  return (
    <AuthLayout
      title={t('auth.b2bResetPassword.title')}
      subtitle={
        <>
          {t('auth.b2bResetPassword.subtitle')} (
          <span className="text-foreground font-semibold">{email}</span>)
        </>
      }
    >
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="password">
                  {t('auth.common.newPassword')}
                </Label>
                <PasswordInput
                  id="password"
                  dir="ltr"
                  autoComplete="new-password"
                  disabled={isSubmitSuccessful}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <p className="text-muted-foreground text-sm">
                  {t('auth.b2bResetPassword.passwordHint')}
                </p>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="confirmPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="confirmPassword">
                  {t('auth.common.confirmPassword')}
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  dir="ltr"
                  autoComplete="new-password"
                  disabled={isSubmitSuccessful}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Subscribe
            selector={(state) => state.isSubmitSuccessful}
            children={(isSubmitSuccessful) =>
              isSubmitSuccessful && (
                <div className="text-center text-green-500">
                  {t('auth.b2bResetPassword.success')}
                </div>
              )
            }
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <TheButton
                type="submit"
                loading={isSubmitting}
                disabled={!canSubmit || isSubmitSuccessful}
                className="w-full cursor-pointer"
              >
                {t('auth.b2bResetPassword.submit')}
              </TheButton>
            )}
          />

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />

          <div className="text-center">
            <Link to="/b2b-login" className="text-foreground text-sm">
              {t('auth.common.returnToLogin')}
            </Link>
          </div>
        </form>
      </CardContent>
    </AuthLayout>
  );
}

export function B2BResetPasswordError() {
  const error = useRouteError();
  const { t } = useTranslation();
  const rawMessage =
    error instanceof Error
      ? error.message
      : 'auth.b2bResetPassword.invalidLinkFallback';
  const errorMessage = t(rawMessage);

  return (
    <AuthLayout title={t('auth.b2bResetPassword.title')}>
      <CardContent>
        <div className="space-y-4">
          <div className="text-center text-red-500">{errorMessage}</div>
          <div className="text-center">
            <Link
              to="/b2b-forgot-password"
              className="text-foreground underline"
            >
              {t('auth.b2bResetPassword.backToForgot')}
            </Link>
          </div>
        </div>
      </CardContent>
    </AuthLayout>
  );
}
